import { listUserSessions, revokeAllUserSessions, revokeUserSession } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { SessionListItem, UserListItem } from '@jq4race/shared';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Skeleton } from '@jq4race/ui';
import { useCallback, useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { formatDateTime } from '../../../../shared/format/format-date';

interface SessionsModalProps {
  user: UserListItem;
  onClose: () => void;
  onChange?: () => void;
}

// Device names are UA-derived (browser/platform identifiers), not
// translatable UI copy — only the "Unknown device" fallback is user-facing text.
function summarizeUserAgent(ua: string | null, unknownDeviceLabel: string): string {
  if (!ua) return unknownDeviceLabel;
  if (/iPhone|iPad/i.test(ua)) return /iPad/.test(ua) ? 'iPad · Safari' : 'iPhone · Safari';
  if (/Android/i.test(ua)) return 'Android · Chrome';
  if (/Edg\//i.test(ua)) return 'Edge';
  if (/Chrome\//i.test(ua)) return 'Chrome · Desktop';
  if (/Safari\//i.test(ua)) return 'Safari · Desktop';
  if (/Firefox\//i.test(ua)) return 'Firefox · Desktop';
  return ua.slice(0, 40);
}

export function SessionsModal({ user, onClose, onChange }: SessionsModalProps) {
  const { t, i18n } = useTranslation('users');
  const [sessions, setSessions] = useState<SessionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingOn, setActingOn] = useState<string | null>(null);
  const [revokingAll, setRevokingAll] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listUserSessions(user.id);
      setSessions(result.sessions);
    } catch (e) {
      setError(translateApiError(e, t, { fallback: t('sessionsModal.loadError') }));
    } finally {
      setLoading(false);
    }
  }, [user.id, t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function revokeOne(sessionId: string) {
    setActingOn(sessionId);
    setError(null);
    try {
      await revokeUserSession(user.id, sessionId);
      onChange?.();
      await reload();
    } catch (e) {
      setError(translateApiError(e, t, { fallback: t('sessionsModal.revokeError') }));
    } finally {
      setActingOn(null);
    }
  }

  async function revokeAll() {
    if (!window.confirm(t('sessionsModal.revokeAllConfirm', { email: user.email }))) return;
    setRevokingAll(true);
    setError(null);
    try {
      await revokeAllUserSessions(user.id);
      onChange?.();
      await reload();
    } catch (e) {
      setError(translateApiError(e, t, { fallback: t('sessionsModal.revokeAllError') }));
    } finally {
      setRevokingAll(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <p className="text-[12px] uppercase tracking-widest text-jq-graphite">
              {t('sessionsModal.activeSessions')}
            </p>
            <CardTitle>{user.email}</CardTitle>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t('sessionsModal.close')}
          </Button>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
              {error}
            </div>
          )}

          {!loading && sessions.length === 0 ? (
            <p className="py-8 text-center text-jq-graphite">{t('sessionsModal.noSessions')}</p>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-2 text-xs font-medium text-jq-graphite">
                    {t('sessionsModal.device')}
                  </th>
                  <th className="text-left py-2 px-2 text-xs font-medium text-jq-graphite">
                    {t('sessionsModal.ip')}
                  </th>
                  <th className="text-left py-2 px-2 text-xs font-medium text-jq-graphite">
                    {t('sessionsModal.lastSeen')}
                  </th>
                  <th className="text-right py-2 px-2 text-xs font-medium text-jq-graphite"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? <SessionsSkeletonRows /> : null}
                {!loading &&
                  sessions.map((s) => {
                    const busy = actingOn === s.id;
                    return (
                      <tr key={s.id} className="border-b">
                        <td className="py-3 px-2 text-sm">
                          {summarizeUserAgent(s.userAgent, t('sessionsModal.unknownDevice'))}
                          {s.isCurrent && (
                            <Badge variant={'success' as 'default'} className="ml-2">
                              {t('sessionsModal.thisSession')}
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-2 text-sm text-jq-graphite">{s.ip ?? '—'}</td>
                        <td className="py-3 px-2 text-sm text-jq-graphite">
                          {formatDateTime(s.lastSeenAt, i18n.language)}
                        </td>
                        <td className="py-3 px-2 text-right">
                          {!s.isCurrent && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => revokeOne(s.id)}
                              disabled={busy}
                            >
                              {busy ? t('sessionsModal.revoking') : t('sessionsModal.revoke')}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          )}

          {sessions.length > 0 && (
            <div className="mt-6 flex items-center justify-between">
              <p className="text-xs text-jq-graphite">{t('sessionsModal.revokedNotice')}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={revokeAll}
                disabled={revokingAll || sessions.every((s) => s.isCurrent)}
              >
                {revokingAll ? t('sessionsModal.revoking') : t('sessionsModal.revokeAll')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SessionsSkeletonRows() {
  return (
    <>
      {Array.from({ length: 3 }, (_, r) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: decorative skeleton rows are static
        <tr key={`session-skeleton-${r}`} className="border-b">
          <td className="py-3 px-2">
            <Skeleton className="h-4 w-[180px]" />
          </td>
          <td className="py-3 px-2">
            <Skeleton className="h-4 w-[100px]" />
          </td>
          <td className="py-3 px-2">
            <Skeleton className="h-4 w-[120px]" />
          </td>
          <td className="py-3 px-2 text-right">
            <Skeleton className="ml-auto h-4 w-[60px]" />
          </td>
        </tr>
      ))}
    </>
  );
}
